import { Link, useNavigate } from 'react-router';
import { DatatableCell, DatatableRow } from '@monksflow/monks-ui';
import { MailIcon } from 'lucide-react';
import type { TemplateItem } from '@/features/templates-config';
import { TableIconBadge } from '@/shared/ui/data-table';
import { ROUTES } from '@/shared/config/routes';
import { formatTableTimestamp } from '@/shared/utils/format';
import { EmailTemplatesActionMenu } from '../EmailTemplatesActionMenu';

interface EmailTemplatesTableRowProps {
  template: TemplateItem;
  onRename: (slug: string, templateName: string) => void;
  onDelete: (slug: string) => void;
}

export function EmailTemplatesTableRow({
  template,
  onRename,
  onDelete,
}: EmailTemplatesTableRowProps) {
  const navigate = useNavigate();

  const handleRowClick = () => {
    void navigate(ROUTES.KNOWLEDGE_HUB_TEMPLATE_DETAIL(template.slug));
  };

  return (
    <DatatableRow
      interactive
      data-testid="email-template-row"
      className="h-20"
      onClick={handleRowClick}
    >
      <DatatableCell>
        <div className="flex items-center gap-3">
          <TableIconBadge>
            <MailIcon className="size-5 text-foreground" aria-hidden="true" />
          </TableIconBadge>
          {/* A real link, not a click handler on the row: `DatatableRow` renders a bare `<tr>` and
              its `interactive` prop is styling only, so row-click alone left no way in from the
              keyboard. This also buys ⌘/middle-click. Adding `tabIndex`/`role="button"` to the row
              instead would override the `<tr>`'s implicit `row` role and break table semantics for
              the screen-reader users this is meant to serve. */}
          <Link
            to={ROUTES.KNOWLEDGE_HUB_TEMPLATE_DETAIL(template.slug)}
            className="rounded-sm text-sm font-medium text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            onClick={(event) => event.stopPropagation()}
          >
            {template.templateName}
          </Link>
        </div>
      </DatatableCell>
      <DatatableCell data-testid="email-template-updated-at">
        <span className="text-sm text-muted-foreground">
          {formatTableTimestamp(template.updatedAt)}
        </span>
      </DatatableCell>
      {/* Balancing spacer — matches the header spacer (see table-layout.constants). */}
      <DatatableCell aria-hidden />
      <DatatableCell className="w-12">
        <EmailTemplatesActionMenu
          slug={template.slug}
          templateName={template.templateName}
          onRename={onRename}
          onDelete={onDelete}
        />
      </DatatableCell>
    </DatatableRow>
  );
}
